/**
 * Golf Shaft Models Database
 * Common shaft pairings for Drivers, Woods, and Hybrids
 */

export interface ShaftModel {
  id: string;
  name: string;
  brand: string;
  category: 'driver' | 'wood' | 'hybrid' | 'iron';
  commonPairings: string[]; // Brand/model combinations this shaft is commonly paired with
}

export const SHAFT_MODELS: ShaftModel[] = [
  // Fujikura Ventus Series
  {
    id: 'ventus-blue',
    name: 'Fujikura Ventus Blue',
    brand: 'Fujikura',
    category: 'driver',
    commonPairings: ['TaylorMade', 'Callaway', 'Titleist', 'Ping']
  },
  {
    id: 'ventus-black',
    name: 'Fujikura Ventus Black',
    brand: 'Fujikura',
    category: 'driver',
    commonPairings: ['TaylorMade', 'Titleist', 'Ping']
  },
  {
    id: 'ventus-red',
    name: 'Fujikura Ventus Red',
    brand: 'Fujikura',
    category: 'driver',
    commonPairings: ['TaylorMade', 'Callaway']
  },
  {
    id: 'ventus-blue-tr',
    name: 'Fujikura Ventus Blue TR',
    brand: 'Fujikura',
    category: 'driver',
    commonPairings: ['TaylorMade Stealth 2', 'TaylorMade Qi35']
  },
  {
    id: 'ventus-black-tr',
    name: 'Fujikura Ventus Black TR',
    brand: 'Fujikura',
    category: 'driver',
    commonPairings: ['TaylorMade Stealth 2', 'Titleist TSR3']
  },
  {
    id: 'ventus-red-tr',
    name: 'Fujikura Ventus Red TR',
    brand: 'Fujikura',
    category: 'driver',
    commonPairings: ['TaylorMade Stealth 2']
  },
  
  // Project X HZRDUS Series
  {
    id: 'hzrdus-smoke-black',
    name: 'Project X HZRDUS Smoke Black',
    brand: 'Project X',
    category: 'driver',
    commonPairings: ['TaylorMade', 'Callaway', 'Cobra']
  },
  {
    id: 'hzrdus-smoke-red',
    name: 'Project X HZRDUS Smoke Red',
    brand: 'Project X',
    category: 'driver',
    commonPairings: ['TaylorMade', 'Callaway']
  },
  {
    id: 'hzrdus-smoke-rdx',
    name: 'Project X HZRDUS Smoke RDX',
    brand: 'Project X',
    category: 'driver',
    commonPairings: ['TaylorMade Stealth 2', 'Callaway Paradym']
  },
  {
    id: 'hzrdus-evenflow',
    name: 'Project X EvenFlow',
    brand: 'Project X',
    category: 'driver',
    commonPairings: ['Callaway', 'Cobra']
  },
  
  // Mitsubishi Tensei Series
  {
    id: 'tensei-av-blue',
    name: 'Mitsubishi Tensei AV Blue',
    brand: 'Mitsubishi',
    category: 'driver',
    commonPairings: ['TaylorMade', 'Titleist', 'Ping']
  },
  {
    id: 'tensei-av-orange',
    name: 'Mitsubishi Tensei AV Orange',
    brand: 'Mitsubishi',
    category: 'driver',
    commonPairings: ['TaylorMade', 'Titleist']
  },
  {
    id: 'tensei-av-white',
    name: 'Mitsubishi Tensei AV White',
    brand: 'Mitsubishi',
    category: 'driver',
    commonPairings: ['Titleist', 'Ping']
  },
  {
    id: 'tensei-ck-pro-blue',
    name: 'Mitsubishi Tensei CK Pro Blue',
    brand: 'Mitsubishi',
    category: 'driver',
    commonPairings: ['Titleist', 'Ping']
  },
  
  // Aldila Series
  {
    id: 'aldila-rogue',
    name: 'Aldila Rogue',
    brand: 'Aldila',
    category: 'driver',
    commonPairings: ['Callaway', 'Cobra', 'Ping']
  },
  {
    id: 'aldila-rogue-silver',
    name: 'Aldila Rogue Silver',
    brand: 'Aldila',
    category: 'driver',
    commonPairings: ['Callaway', 'Ping']
  },
  {
    id: 'aldila-nv',
    name: 'Aldila NV',
    brand: 'Aldila',
    category: 'driver',
    commonPairings: ['Callaway', 'Cobra']
  },
  
  // Fujikura Speeder Series
  {
    id: 'fujikura-speeder',
    name: 'Fujikura Speeder',
    brand: 'Fujikura',
    category: 'driver',
    commonPairings: ['TaylorMade', 'Titleist']
  },
  {
    id: 'fujikura-speeder-evolution',
    name: 'Fujikura Speeder Evolution',
    brand: 'Fujikura',
    category: 'driver',
    commonPairings: ['TaylorMade', 'Titleist']
  },
  
  // KBS Series (more common in irons but some driver options)
  {
    id: 'kbs-pgi',
    name: 'KBS PGI',
    brand: 'KBS',
    category: 'driver',
    commonPairings: ['Cobra', 'Wilson']
  },
  
  // Graphite Design Series
  {
    id: 'graphite-design-tour-ad',
    name: 'Graphite Design Tour AD',
    brand: 'Graphite Design',
    category: 'driver',
    commonPairings: ['Titleist', 'Ping', 'PXG']
  },
  
  // Fairway Wood Shafts
  {
    id: 'ventus-blue-fw',
    name: 'Fujikura Ventus Blue (Fairway)',
    brand: 'Fujikura',
    category: 'wood',
    commonPairings: ['TaylorMade', 'Callaway', 'Titleist']
  },
  {
    id: 'hzrdus-smoke-black-fw',
    name: 'Project X HZRDUS Smoke Black (Fairway)',
    brand: 'Project X',
    category: 'wood',
    commonPairings: ['TaylorMade', 'Callaway']
  },
  {
    id: 'tensei-av-blue-fw',
    name: 'Mitsubishi Tensei AV Blue (Fairway)',
    brand: 'Mitsubishi',
    category: 'wood',
    commonPairings: ['TaylorMade', 'Titleist', 'Ping']
  },
  {
    id: 'aldila-rogue-fw',
    name: 'Aldila Rogue (Fairway)',
    brand: 'Aldila',
    category: 'wood',
    commonPairings: ['Callaway', 'Ping']
  },
  
  // Hybrid Shafts
  {
    id: 'ventus-blue-hybrid',
    name: 'Fujikura Ventus Blue (Hybrid)',
    brand: 'Fujikura',
    category: 'hybrid',
    commonPairings: ['TaylorMade', 'Callaway', 'Titleist']
  },
  {
    id: 'hzrdus-smoke-black-hybrid',
    name: 'Project X HZRDUS Smoke Black (Hybrid)',
    brand: 'Project X',
    category: 'hybrid',
    commonPairings: ['TaylorMade', 'Callaway']
  },
  {
    id: 'tensei-av-blue-hybrid',
    name: 'Mitsubishi Tensei AV Blue (Hybrid)',
    brand: 'Mitsubishi',
    category: 'hybrid',
    commonPairings: ['TaylorMade', 'Titleist', 'Ping']
  },
  {
    id: 'aldila-rogue-hybrid',
    name: 'Aldila Rogue (Hybrid)',
    brand: 'Aldila',
    category: 'hybrid',
    commonPairings: ['Callaway', 'Ping']
  },
];

/**
 * Get shaft models for a specific club type
 */
export const getShaftModelsForClubType = (
  clubType: string
): ShaftModel[] => {
  if (clubType === 'driver') {
    return SHAFT_MODELS.filter(s => s.category === 'driver');
  }
  if (clubType.includes('wood')) {
    return SHAFT_MODELS.filter(s => s.category === 'wood' || s.category === 'driver');
  }
  if (clubType.includes('hybrid')) {
    return SHAFT_MODELS.filter(s => s.category === 'hybrid' || s.category === 'driver');
  }
  return [];
};

/**
 * Get shaft models commonly paired with a specific brand/model
 */
export const getShaftModelsForBrandModel = (
  brand: string,
  model: string,
  clubType: string
): ShaftModel[] => {
  const brandModel = `${brand} ${model}`;
  return SHAFT_MODELS.filter(shaft => 
    (shaft.category === 'driver' && clubType === 'driver') ||
    (shaft.category === 'wood' && clubType.includes('wood')) ||
    (shaft.category === 'hybrid' && clubType.includes('hybrid'))
  ).filter(shaft =>
    shaft.commonPairings.some(pairing => 
      pairing.toLowerCase().includes(brand.toLowerCase()) ||
      pairing.toLowerCase().includes(brandModel.toLowerCase())
    )
  );
};

/**
 * Get all unique shaft model names for dropdown
 */
export const getAllShaftModelNames = (clubType: string): string[] => {
  const models = getShaftModelsForClubType(clubType);
  const uniqueNames = Array.from(new Set(models.map(m => m.name)));
  return uniqueNames.sort();
};

